import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { mergeOverrides } from 'baseui'
import { ButtonGroup, SIZE } from 'baseui/button-group'
import { Button } from 'baseui/button'
import {
  SDButtonGroupButtonOverrides,
  SDButtonGroupButtonPulseOverrides,
  SDButtonGroupOverrides,
  SDButtonGroupPulseOverrides,
} from './styled-components'
import usePulseScope from '@/shared/components/PulseScope/usePulseScope'
import PulsePillGroup from './PulsePillGroup/PulsePillGroup'

const SDButtonGroup = ({
  disabledOptions,
  onClickHandler,
  options,
  selectedValue,
  size,
  newTranslations = false,
  translationsPath = '',
  direction,
  error,
  missing = false,
  stretch = true,
  overrides,
  ...rest
}) => {
  const { t } = useTranslation()
  const isPulse = usePulseScope()
  const selected = []
  const buttons = []
  const withPill = isPulse && !Array.isArray(selectedValue)
  let selectedDisabled = false

  if (options.length > 0) {
    options.forEach((option, index) => {
      Object.keys(option).forEach(key => {
        const isDisabled = disabledOptions?.includes(key)

        if (Array.isArray(selectedValue)) {
          if (selectedValue.includes(key)) {
            selected.push(index)
          }
        } else if (key === selectedValue) {
          selected.push(index)
          selectedDisabled = Boolean(isDisabled)
        }

        buttons.push(
          <ButtonGroupButton
            id={`checkbox-option-${key}`}
            key={key}
            withPill={withPill}
            stretch={stretch}
            disabled={isDisabled}
            onClick={event => {
              event.preventDefault()
              onClickHandler(key)
            }}
          >
            {newTranslations ? t(translationsPath + key) : option[key]}
          </ButtonGroupButton>,
        )
      })
    })
  }

  const baseOverrides = isPulse
    ? SDButtonGroupPulseOverrides(direction, error, missing)
    : SDButtonGroupOverrides(direction, error)
  const newOverrides = mergeOverrides(baseOverrides, overrides)

  return (
    <PulsePillGroup
      enabled={withPill}
      selectedIndex={selected[0]}
      disabled={Boolean(rest.disabled || selectedDisabled)}
    >
      <ButtonGroup {...rest} size={size ? SIZE[size] : SIZE.compact} overrides={newOverrides} selected={selected}>
        {buttons}
      </ButtonGroup>
    </PulsePillGroup>
  )
}

// eslint-disable-next-line react/display-name
const ButtonGroupButton = React.forwardRef(({ withPill = false, stretch = true, ...props }, ref) => {
  const isPulse = usePulseScope()
  const baseOverrides = isPulse ? SDButtonGroupButtonPulseOverrides(withPill, stretch) : SDButtonGroupButtonOverrides()
  const newOverrides = mergeOverrides(baseOverrides, props.overrides)
  return (
    <Button
      {...props}
      ref={ref}
      data-label={isPulse && typeof props.children === 'string' ? props.children : undefined}
      overrides={newOverrides}
    >
      {props.children}
    </Button>
  )
})

SDButtonGroup.propTypes = {
  disabledOptions: PropTypes.array,
  newTranslations: PropTypes.bool,
  translationsPath: PropTypes.string,
  onClickHandler: PropTypes.func,
  options: PropTypes.array.isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  size: PropTypes.string,
  direction: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  missing: PropTypes.bool,
  stretch: PropTypes.bool,
  overrides: PropTypes.object,
}

ButtonGroupButton.propTypes = {
  withPill: PropTypes.bool,
  stretch: PropTypes.bool,
  overrides: PropTypes.object,
  children: PropTypes.node,
}

export default SDButtonGroup
